import {useState} from 'react';
import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Card,
    Divider,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {ProviderConnection} from '../types';
import {
    presentStatus,
    rendersExternalIdPanel,
    statusColor,
} from '../statusPresentation';
import {ConnectionStatusBadge} from './ConnectionStatusBadge';
import {CapabilitySnapshot} from './CapabilitySnapshot';
import {ExternalIdPanel} from './ExternalIdPanel';
import {RevalidateDrawer} from '../drawers/RevalidateDrawer';
import {DisconnectProviderModal} from './DisconnectProviderModal';
import {RenameDrawer} from '../drawers/RenameDrawer';
import {DeclareCapabilitiesDrawer} from '../drawers/DeclareCapabilitiesDrawer';
import {useProviderDisconnect} from '../hooks/useProviderDisconnect';
import {DetailPageHeader} from './DetailPageHeader';

// FNP-1248 — THE DETAIL VIEW.
//
// Four things, in the order a tenant needs them:
//   1. STATUS, with the provider's `statusDetail` verbatim.
//   2. THE EXTERNAL ID, when this is an ASSUMED_ROLE connection.
//   3. THE CAPABILITY SNAPSHOT, read-only rendered as read-only.
//   4. THE ACTIONS — re-validate, and disconnect behind its preview.
//
// ⚠️ FNP-1286 — WHO SAYS WHAT, ONCE.
//
// Sections 1 and 2 used to explain the same state to each other's face: the
// status card printed `presentation.summary` and the provider's verbatim
// message, and the panel immediately below printed BOTH AGAIN. The tenant read
// the same paragraph twice and the same provider error twice on the first screen
// they ever see.
//
// `rendersExternalIdPanel` settles it in one place. When the panel is on screen
// it owns the explanation and the provider's words, because it is where the
// tenant acts — the reason and the value they must copy belong together. When it
// is not (an API_TOKEN connection, or an ASSUMED_ROLE one with no id), the status
// card carries them, so no state is ever left as a bare badge with no reason.
//
// ⚠️ THE COPY IS UNCHANGED. Both sentences still say exactly what they said;
// only one of them renders.

export const Profile = (
    {
        connection,
        onDisconnected,
    }: {
        connection: ProviderConnection,
        // Called after a confirmed disconnect, so the page can leave — the
        // record this view is built on no longer exists.
        onDisconnected: () => void,
    },
) => {

    const [revalidateOpened, setRevalidateOpened] = useState(false);
    const [renameOpened, setRenameOpened] = useState(false);
    const [declareOpened, setDeclareOpened] = useState(false);

    const presentation = presentStatus(connection);

    // FNP-1286 — when the panel renders, it is the thing that explains this
    // state, and the status card must not say it a second time.
    const panelOwnsTheExplanation = rendersExternalIdPanel(connection);

    const {
        connection: disconnectTarget,
        opened: disconnectOpened,
        requestDisconnect,
        cancelDisconnect,
        confirmDisconnect,
        preview,
        isPreviewLoading,
        isPreviewError,
        retryPreview,
        isDisconnecting,
        canConfirm,
    } = useProviderDisconnect();

    const handleConfirmDisconnect = async () => {
        await confirmDisconnect();
        onDisconnected();
    };

    return (
        <Stack gap={'lg'} p={'md'}>

            {/* FNP-1305 — the header owns the actions; this component owns the
                state they act on, which is why it renders the header rather
                than the page doing so. */}
            <DetailPageHeader
                connection={connection}
                onRename={() => setRenameOpened(true)}
                onEditPermissions={() => setDeclareOpened(true)}
                onRevalidate={() => setRevalidateOpened(true)}
                onDisconnect={() => requestDisconnect(connection)}
            />

            {/* ─── 1. STATUS ─── */}
            <Card withBorder={true} padding={'lg'}>
                <Stack gap={'md'}>

                    {/* ⚠️ FNP-1305 — NO DISPLAY NAME HERE. The header above now
                        carries it as the page title, and printing it again three
                        lines below is FNP-1286's defect in a new place: found on
                        the first browser check of this rework, invisible to the
                        tests that pin the header contract, because each of them
                        was true on its own. The provider and the status badge
                        stay — neither is in the header. */}
                    <Group justify={'space-between'} align={'flex-start'}>
                        <Text size={'sm'} c={'dimmed'}>
                            {I18n.get('Provider')}: {connection.providerId}
                        </Text>
                        <ConnectionStatusBadge connection={connection}/>
                    </Group>

                    {/* ⚠️ THE WHOLE ALERT GOES when the panel below is carrying
                        the explanation. Keeping an empty titled Alert would
                        leave a coloured box restating the badge beside it and
                        nothing else — the badge already names the state, and a
                        box saying only what the badge says is noise the tenant
                        reads before reaching the value they came for. */}
                    {!panelOwnsTheExplanation && (
                        <Alert
                            color={statusColor(presentation.tone)}
                            variant={'light'}
                            title={presentation.label}
                        >
                            <Stack gap={'xs'}>
                                <Text size={'sm'}>{presentation.summary}</Text>

                                {/* ⚠️ THE PROVIDER'S OWN WORDS, VERBATIM AND
                                    UNTRUNCATED. Never re-worded on the way to
                                    the screen — classify for the core, preserve
                                    for the human (FNP-1180). FNP-1087 took a
                                    whole investigation because a normalised
                                    class was all anyone had.

                                    Rendered HERE only when no external-id panel
                                    is on screen to render it instead; the panel
                                    shows the same string, in the same verbatim
                                    form, beside the id (FNP-1286). */}
                                {connection.statusDetail && (
                                    <Stack gap={2}>
                                        <Text size={'xs'} c={'dimmed'} fw={500}>
                                            {I18n.get('Provider response')}
                                        </Text>
                                        <Text size={'xs'} c={'dimmed'} data-testid={'status-detail'}>
                                            {connection.statusDetail}
                                        </Text>
                                    </Stack>
                                )}
                            </Stack>
                        </Alert>
                    )}

                </Stack>
            </Card>

            {/* ─── 2. THE EXTERNAL ID ───
                Renders itself to null for an API_TOKEN connection, which has
                none. No role ARN is passed: it is not stored on the connection
                record, so on a page load there is genuinely none to show and
                inventing one would be worse than omitting the line. */}
            <ExternalIdPanel connection={connection}/>

            {/* ─── 3. CAPABILITIES ─── */}
            <Card withBorder={true} padding={'lg'}>
                <CapabilitySnapshot connection={connection}/>
            </Card>

            {/* ─── 4. WHAT THESE ACTIONS DO ─── */}
            {/* ⚠️ FNP-1305 — THE BUTTONS MOVED TO THE HEADER TOOLS ARRAY; THIS
                COPY DID NOT MOVE WITH THEM, AND THAT IS DELIBERATE.

                A toolbar button can carry a verb and nothing else. Each of these
                three actions has a consequence a tenant needs before clicking —
                that a rename re-checks nothing and keeps the external ID, that
                re-validating does not mint a new one, that disconnecting deletes
                a credential permanently. Dropping the sentences to satisfy the
                toolbar contract would have traded a layout rule for the
                information the page exists to give.

                So the controls live in the header, where the checklist wants
                them, and their explanations stay on the page, ordered
                least-destructive-first as before. */}
            <Card withBorder={true} padding={'lg'}>
                <Stack gap={'md'}>

                    <Text fw={500} size={'sm'}>{I18n.get('What you can do')}</Text>

                    <Stack gap={4}>
                        <Text fw={500} size={'sm'}>{I18n.get('Rename')}</Text>
                        <Text size={'sm'} c={'dimmed'}>
                            {productCopy('Change how this connection is labelled in {product}. Nothing is re-checked, the status does not change, and your external ID stays the same.')}
                        </Text>
                    </Stack>

                    <Divider my={'xs'}/>

                    {/* ⚠️ FNP-1269 — THE ASSUMED_ROLE SENTENCE NO LONGER ASKS
                        FOR ANYTHING. It used to end "Submit the same role ARN",
                        which was wrong twice over: the field was never
                        prefilled, so "the same" was a retype from memory, and a
                        typo in that retype silently repointed the connection at
                        a different role. Re-validating now sends an id and asks
                        Flexi to re-check the role already on file, so the copy
                        says what the button does — nothing to re-enter.

                        An API_TOKEN re-validate IS still a rotation and still
                        asks for the new token, so its sentence is unchanged. */}
                    <Stack gap={4}>
                        <Text fw={500} size={'sm'}>{I18n.get('Re-validate')}</Text>
                        <Text size={'sm'} c={'dimmed'}>
                            {connection.credentialType === 'ASSUMED_ROLE'
                                ? productCopy("Once the external ID is in your role's trust policy, re-validate to have {product} check the connection again. There is nothing to re-enter — {product} uses the role it already has on file, and your external ID does not change.")
                                : I18n.get('Supply a fresh API token to replace the stored one.')}
                        </Text>
                    </Stack>

                    <Divider my={'xs'}/>

                    <Stack gap={4}>
                        <Text fw={500} size={'sm'}>{I18n.get('Disconnect')}</Text>
                        <Text size={'sm'} c={'dimmed'}>
                            {productCopy('Removes this connection and deletes its stored credential. {product} will show you what this affects before anything is removed.')}
                        </Text>
                    </Stack>

                </Stack>
            </Card>

            {renameOpened && (
                <RenameDrawer
                    connection={connection}
                    opened={renameOpened}
                    onClose={() => setRenameOpened(false)}
                />
            )}

            {/* ⚠️ MOUNTED ONLY WHILE OPEN, like its siblings, and here that is
                load-bearing rather than a tidiness choice. The drawer seeds its
                checkboxes from the connection's CURRENT declaration in
                `useState`, which runs once per mount — so unmounting on close
                is what makes a second open read the corrected values instead of
                the ones from the first. */}
            {declareOpened && (
                <DeclareCapabilitiesDrawer
                    connection={connection}
                    opened={declareOpened}
                    onClose={() => setDeclareOpened(false)}
                />
            )}

            {revalidateOpened && (
                <RevalidateDrawer
                    connection={connection}
                    opened={revalidateOpened}
                    onClose={() => setRevalidateOpened(false)}
                />
            )}

            <DisconnectProviderModal
                connection={disconnectTarget}
                preview={preview}
                opened={disconnectOpened}
                onClose={cancelDisconnect}
                onConfirm={handleConfirmDisconnect}
                canConfirm={canConfirm}
                isPreviewLoading={isPreviewLoading}
                isPreviewError={isPreviewError}
                onRetryPreview={retryPreview}
                isDisconnecting={isDisconnecting}
            />

        </Stack>
    );
};
